"use client";

import { TextField } from "../../TextField";
import {
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { AutoNumberFieldProps } from "../lib/type";
import { Box, IconButton } from "@mui/material";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { MaskedField } from "../../MaskedField";
import { ButtonLoader } from "@/src/components/Loaders";
import { transliterate } from "@/src/components/Fields/AutoNumberField/lib/transliterate.helper";
import { ChangeEvent, useCallback, useEffect } from "react";
import { isString } from "lodash";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import {useParams} from "next/navigation";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";

export const AutoNumberField = <T extends FieldValues>({
  defaultValue = "",
  hintIcon = false,
  mask = "AA0001AA",
  definitions = {
    A: /[АВСЕGРRQUVHMXTOKIJDFSZYABCEPНМХТОКІГДПЯЗЦБИЛЭФЖУЧЮЩ]/i,
    "0": /[0-9]/,
  },
  formatValue = (value) => value.replaceAll(" ", "").toUpperCase(),
  placeholder,
  toggleHintArea,
  label,
  closeHintArea,
  openHintArea,
  noStyle,
  loading = true,
  ...rest
}: AutoNumberFieldProps<T>) => {
    const { productSlug } = useParams<{ productSlug: string }>();
    const { callProductAnalyticEvent } = useAnalytics();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const formattedValue = formatValue(rawValue);
      rest.onChange?.({
        ...event,
        target: { ...event.target, value: formattedValue },
      } as PathValue<T, Path<T>>);
    },
    [formatValue, rest],
  );
  const name = rest.name;
  const { setValue, getValues, control } = useFormContext();
  const currentValue = useWatch({ name, control });

  useEffect(() => {
    const rawValue = getValues(name);
    const formattedValue = formatValue(rawValue || "");
    if (rawValue !== formattedValue && isString(name)) {
      setValue(name as string, formattedValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [currentValue, name]);

  return (
    <TextField
      {...rest}
        onFocus={() => callProductAnalyticEvent(productSlug, { eventName: "plate_input_focused" })}
      InputProps={{
        inputComponent: MaskedField as any,
        startAdornment: (
          <>
            {!noStyle ? (
              <Box className="flex h-14 w-12 rounded-l">
                <WebShopIcon name="uaAutoNumberAddon" />
              </Box>
            ) : undefined}
          </>
        ),
        endAdornment: loading ? (
          <ButtonLoader size={"large"} className={"mr-3"} />
        ) : (
          <>
            {hintIcon && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHintArea?.();
                }}
                className={cn("px-2", {
                  hidden: !hintIcon || !noStyle,
                  flex: hintIcon,
                  "md:hidden": true,
                })}
              >
                <WebShopIcon name={"questionCircle"} />
              </IconButton>
            )}
          </>
        ),
        inputProps: {
          mask,
          definitions,
          autoComplete: "off",
          className: !noStyle ? "text-center text-[28px] p-0" : undefined,
        },
        disableUnderline: true,
      }}
      label={noStyle && label}
      placeholder={placeholder}
      sx={
        !noStyle
          ? {
              "& .MuiFilledInput-root": {
                height: "58px",
                borderColor: "var(--dark-blue)",
                borderWidth: "2px",
                paddingRight: "8px",
                "&:hover": {
                  borderColor: "var(--dark-blue)",
                  borderWidth: "2px!important",
                },
                "&.Mui-focused": {
                  borderColor: "var(--dark-blue)",
                  boxShadow: "none",
                  borderWidth: "2px",
                },
                "& .MuiInputBase-input": {
                  padding: "0px !important",
                  borderWidth: "2px",
                },
              },
            }
          : undefined
      }
      formatValue={formatValue}
      hintIcon={hintIcon}
      defaultValue={formatValue(defaultValue) as PathValue<T, Path<T>>}
      toggleHintArea={toggleHintArea}
      closeHintArea={closeHintArea}
      openHintArea={openHintArea}
      onChange={handleChange}
    />
  );
};
